import React, { PropsWithChildren } from 'react';
import BaseModel from '../model/BaseModel';

import { ServiceApi, WhereCondition } from "../service/ServiceApi";
import CacheRecord from '../utils/CacheRecord';
import PromiseQueue, { CancelableItem } from '../utils/PromiseQueue';

export interface EntityNameProps<T> {
    entity: string;
    entityId: string;
    entityField?: keyof T | null;
    propMap?: (item: T) => React.ReactNode;
    cache?: boolean;
    cacheKey?: string;
}

interface EntityNameState {
    name?: React.ReactNode | null;
}

export default class EntityName<T extends BaseModel> extends React.Component<PropsWithChildren<EntityNameProps<T>>, EntityNameState> {

    static cache: Record<string, CacheRecord<React.ReactNode>> = {};
    static promiseQueue: PromiseQueue<any, React.ReactNode> = new PromiseQueue<any, React.ReactNode>();

    public entityApi: ServiceApi<T>;
    private promiseCancel: CancelableItem | null = null;

    constructor(props: EntityNameProps<T>) {
        super(props);
        const { entity } = props;
        this.state = {
            name: null
        }
        this.entityApi = new ServiceApi<T>(entity);
        this.getName = this.getName.bind(this);
        this.setName = this.setName.bind(this);
    }

    componentDidMount() {
        this.promiseCancel = EntityName.promiseQueue.enqueue(null, this.getName, this.setName);
    }

    componentWillUnmount() {
        this.promiseCancel?.cancel();
    }

    async getEntity(): Promise<T | undefined> {
        const { entityField, entityId } = this.props;
        if (typeof entityId === 'string' || typeof entityId === 'number') {
            if (!entityField || entityField === "id") {
                return await this.entityApi.getById(entityId);
            } else if (entityField) {
                const where: WhereCondition<T> = [
                    [entityField, '=', entityId]
                ];
                const list = await this.entityApi.getList({ where });
                return list.length === 1 ? list[0] : undefined;
            }
        }
    }

    async getName(): Promise<React.ReactNode> {
        const { propMap, entity, entityId, cacheKey } = this.props;
        var name: React.ReactNode = null;

        const _cacheKey = cacheKey || entity;

        if (this.props.cache && _cacheKey in EntityName.cache) {
            var cache = EntityName.cache[_cacheKey];
            const cacheResult = cache.getValue(entityId);
            if (cacheResult) return cacheResult;
        }

        const item = await this.getEntity();
        if (item) {
            name = propMap?.call(this, item) ?? item.name;
            if (this.props.cache && name) {
                if (_cacheKey in EntityName.cache === false) {
                    EntityName.cache[_cacheKey] = new CacheRecord<React.ReactNode>(60 * 1000); //1 minute
                }
                EntityName.cache[_cacheKey].setValue(entityId, name);
            }
        }

        return name;
    }

    setName(name: React.ReactNode) {
        this.setState({ name });
    }

    render() {
        return this.state.name || this.props.children || null;
    }
}